import { useCallback } from 'react';
import { getNodesBounds } from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import useAbsolutePosition from './useAbsolutePosition';
import { groupColors, groupColorsLight } from '../constants/StyleConstants';

const useNodeActions = () => {
  const { handleNodeDragAbsolutePosition, handleDescendant, handleAbsolutePosition } = useAbsolutePosition();

  const handleGroupsOrder = (nodes) => {
    const groupWithoutParent = nodes.find((n) => n.type === 'group' && (!('parentId' in n) || !('extent' in n)));

    if (!groupWithoutParent) return nodes;

    const filtered = nodes.filter((n) => n.id !== groupWithoutParent.id);
    return [groupWithoutParent, ...filtered];
  };

  const handleNodeDragStop = (nodes, draggedNode) => {
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    const groups = nodes.filter((n) => n.type === 'group' && n.id !== draggedNode.id);
    const selectedOrDragged = nodes.filter((n) => n.selected || n.id === draggedNode.id);
    let updatedNodes = [...nodes];

    // eslint-disable-next-line
    for (const node of selectedOrDragged) {
      // Use the event’s absolute position for the dragged node
      const nodeAbsPos =
        node.id === draggedNode.id
          ? {
              x: draggedNode.positionAbsolute?.x ?? draggedNode.position?.x ?? 0,
              y: draggedNode.positionAbsolute?.y ?? draggedNode.position?.y ?? 0
            }
          : handleNodeDragAbsolutePosition(node.id, nodeMap);

      // eslint-disable-next-line
      for (const group of groups) {
        const groupAbsPos = handleNodeDragAbsolutePosition(group.id, nodeMap);
        const width = group.width ?? parseFloat(group.style?.width) ?? 0;
        const height = group.height ?? parseFloat(group.style?.height) ?? 0;
        const withinBounds =
          nodeAbsPos.x >= groupAbsPos.x &&
          nodeAbsPos.x < groupAbsPos.x + width &&
          nodeAbsPos.y >= groupAbsPos.y &&
          nodeAbsPos.y < groupAbsPos.y + height;

        // eslint-disable-next-line
        if (!withinBounds) continue;
        // eslint-disable-next-line
        if (group.id === node.id || handleDescendant(group.id, node.id, nodeMap)) continue;
        if (node.parentId === group.id) break; // already a child

        // Compute position relative to group
        const relativeX = nodeAbsPos.x - groupAbsPos.x;
        const relativeY = nodeAbsPos.y - groupAbsPos.y;

        updatedNodes = updatedNodes.map((n) =>
          n.id === node.id
            ? {
                ...n,
                parentId: group.id,
                extent: 'parent',
                position: { x: relativeX, y: relativeY },
                style: {
                  ...n.style,
                  position: undefined // remove any static CSS positioning
                }
              }
            : n
        );
        break;
      }
    }

    return handleGroupsOrder(updatedNodes);
  };

  const handleGroupToggle = (nodes, setNodes, groupCount, setGroupCount, clearSelection, isDarkMode) => {
    const selected = nodes.filter((n) => n.selected);
    const groupId = `group-${uuidv4()}`;
    const color = isDarkMode
      ? groupColors[groupCount % groupColors.length]
      : groupColorsLight[groupCount % groupColorsLight.length];

    // CASE 1: No nodes selected – create empty group
    if (!selected.length) {
      const emptyGroupNode = {
        id: groupId,
        type: 'group',
        data: { label: `Group ${groupCount + 1}` },
        position: { x: 200, y: 200 },
        width: 200,
        height: 200,
        draggable: true,
        selectable: true,
        style: {
          background: color,
          border: `2px solid ${color}`,
          zIndex: -100 - groupCount
        }
      };
      setNodes((nds) => [emptyGroupNode, ...nds]);
      setGroupCount((count) => count + 1);
      clearSelection();
      return;
    }

    // CASE 2: Ungroup if selected one group
    if (selected.length === 1 && selected[0].type === 'group') {
      const selectedGroupId = selected[0].id;
      setNodes((nds) => {
        const groupNode = nds.find((n) => n.id === selectedGroupId);
        return nds
          .map((n) => {
            if (n.parentId === selectedGroupId && groupNode) {
              return {
                ...n,
                position: {
                  x: n.position.x + groupNode.position.x,
                  y: n.position.y + groupNode.position.y
                },
                parentId: undefined,
                extent: undefined
              };
            }
            return n;
          })
          .filter((n) => n.id !== selectedGroupId); // remove the group node
      });
      clearSelection();
      return;
    }

    // CASE 3: Create a group from selected nodes
    const bounds = getNodesBounds(selected);
    const padding = 20;

    const groupNode = {
      id: groupId,
      type: 'group',
      data: { label: `Group ${groupCount + 1}` },
      position: { x: bounds.x - padding, y: bounds.y - padding },
      width: bounds.width + padding * 2, // set group width/height
      height: bounds.height + padding * 2,
      style: {
        background: color,
        border: `2px solid ${color}`,
        zIndex: -100 - groupCount
      }
    };

    setNodes((nds) => {
      const withGroup = [groupNode].concat(
        nds.map((n) => {
          if (!n.selected) return { ...n, selected: false };
          // Compute child’s absolute position
          const absPos = handleAbsolutePosition(n, nds);
          // Reparent node under the new group with relative position
          return {
            ...n,
            parentId: groupId,
            extent: 'parent',
            position: {
              x: absPos.x - (bounds.x - padding),
              y: absPos.y - (bounds.y - padding)
            },
            selected: false
          };
        })
      );
      return withGroup;
    });

    setGroupCount((count) => count + 1);
    clearSelection();
  };

  const handleUngroupNode = (prevNodes, groupId) => {
    const groupNode = prevNodes.find((n) => n.id === groupId);

    return prevNodes
      .map((n) => {
        if (n.parentId === groupId && groupNode) {
          const absX = n.position.x + groupNode.position.x;
          const absY = n.position.y + groupNode.position.y;

          return {
            ...n,
            position: { x: absX, y: absY },
            parentId: undefined,
            extent: undefined
          };
        }
        return n;
      })
      .filter((n) => n.id !== groupId); // remove the group node if needed
  };

  const handleNodeDetach = (nodes, id) => {
    return nodes.map((n) => {
      if (n.id !== id || !n.parentId) return n;

      const absPos = handleAbsolutePosition(n, nodes);

      return {
        ...n,
        position: absPos,
        parentId: undefined,
        extent: undefined
      };
    });
  };

  const handleGroupZIndices = useCallback((nodes, setNodes) => {
    const groups = nodes.filter((n) => n.type === 'group');

    const sorted = [...groups].sort((a, b) => {
      const areaA = a.style.width * a.style.height;
      const areaB = b.style.width * b.style.height;
      return areaA - areaB;
    });

    setNodes((nds) => {
      let hasChanges = false;

      const updated = nds.map((n) => {
        if (n.type !== 'group') return n;
        const index = sorted.findIndex((g) => g.id === n.id);
        const newZIndex = -100 + index;

        if (n.style?.zIndex !== newZIndex) {
          hasChanges = true;
          return {
            ...n,
            style: {
              ...n.style,
              zIndex: newZIndex
            }
          };
        }

        return n;
      });

      return hasChanges ? updated : nds;
    });
  }, []);

  return {
    handleNodeDragStop,
    handleUngroupNode,
    handleNodeDetach,
    handleGroupZIndices,
    handleGroupToggle
  };
};

export default useNodeActions;
